import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { formatTaskTimeRange, getLocalDateKey } from './dateUtils';
import { getTimelineTaskRange, isAllDayTask } from './scheduleUtils';
import { TimelineTaskModal } from './TimelineTaskModal';
import { PlannerTaskInfo } from './types';
import { TIMELINE_GESTURE_HOLD_DELAY } from './timelineGestureConstants';
import { useTimelineView } from './TimelineViewContext';
import {
    getTaskDaySegment, getTimelineTaskRenderSegment, HourTaskLayout,
} from './timelineViewUtils';

interface TimelineHourTaskProps {
    getOffset: (date: Date) => number;
    layout?: HourTaskLayout;
    periodDate: Date;
    taskInfo: PlannerTaskInfo;
}

export const TimelineHourTask: React.FC<TimelineHourTaskProps> = ({
    getOffset, layout = { column: 0, columnCount: 1 }, periodDate, taskInfo: { task, collectionId },
}) => {
    const { t } = useTranslation();
    const context = useTimelineView();
    const {
        currentMouseY, draggingTask, dragTaskStartTopY, dragTaskStartY, getTimelineDateFromClientY,
        onDeleteTask, onEditTask, onResizeLongPress, onTaskLongPress, onToggleComplete,
        resizingTask, resizeStartY, timeSlotHeight,
    } = context;
    const [showActions, setShowActions] = useState(false);
    const pointerStartY = useRef<number | null>(null);
    const pointerMoved = useRef(false);
    const longPressTimer = useRef<number | null>(null);
    const longPressActive = useRef(false);
    const touchMoveBlocker = useRef<((event: TouchEvent) => void) | null>(null);
    const touchDragElement = useRef<HTMLElement | null>(null);
    const clearLongPress = () => {
        if (longPressTimer.current !== null) window.clearTimeout(longPressTimer.current);
        longPressTimer.current = null;
    };
    const releaseTouchGesture = () => {
        clearLongPress();
        if (touchDragElement.current && touchMoveBlocker.current) {
            touchDragElement.current.removeEventListener('touchmove', touchMoveBlocker.current);
        }
        touchDragElement.current = null;
        touchMoveBlocker.current = null;
        longPressActive.current = false;
    };
    const watchTouchMove = (element: HTMLElement) => {
        const blocker = (event: TouchEvent) => {
            if (longPressActive.current) event.preventDefault();
            else clearLongPress();
        };
        touchDragElement.current = element;
        touchMoveBlocker.current = blocker;
        element.addEventListener('touchmove', blocker, { passive: false });
    };
    const startResizeHold = (edge: 'top' | 'bottom', event: React.PointerEvent<HTMLDivElement>) => {
        event.stopPropagation();
        longPressActive.current = false;
        const element = event.currentTarget;
        const { clientY, pointerId } = event;
        element.setPointerCapture?.(pointerId);
        if (event.pointerType === 'touch') watchTouchMove(element);
        longPressTimer.current = window.setTimeout(() => {
            longPressTimer.current = null;
            longPressActive.current = true;
            pointerMoved.current = true;
            onResizeLongPress(task, collectionId, edge, element, clientY, pointerId);
        }, TIMELINE_GESTURE_HOLD_DELAY);
    };
    useEffect(() => () => {
        if (longPressTimer.current !== null) window.clearTimeout(longPressTimer.current);
        if (touchDragElement.current && touchMoveBlocker.current) {
            touchDragElement.current.removeEventListener('touchmove', touchMoveBlocker.current);
        }
    }, []);
    const timelineRange = getTimelineTaskRange(task);
    const taskDate = timelineRange.start;
    if (Number.isNaN(taskDate.getTime())) return null;
    const allDay = isAllDayTask(task);
    const duration = Math.max(15, (timelineRange.end.getTime() - taskDate.getTime()) / 60_000);
    const dayStart = new Date(periodDate);
    dayStart.setHours(0, 0, 0, 0);
    const carriesIntoFirstLoadedDay = taskDate < dayStart;
    const renderSegment = getTimelineTaskRenderSegment(task, periodDate, carriesIntoFirstLoadedDay)
        || getTaskDaySegment(task, periodDate);
    const renderStart = renderSegment.start;
    const renderDuration = (renderSegment.end.getTime() - renderStart.getTime()) / 60_000;
    const dragging = draggingTask?.task.id === task.id;
    const resizing = resizingTask?.task.id === task.id;
    let top = getOffset(renderStart);
    let height = renderDuration / 60 * timeSlotHeight;
    let displayDate = new Date(renderStart);
    let displayDuration = renderDuration;
    if (!allDay && dragging && currentMouseY !== null && dragTaskStartY !== null && dragTaskStartTopY !== null) {
        const taskTopY = dragTaskStartTopY + currentMouseY - dragTaskStartY;
        const target = getTimelineDateFromClientY(taskTopY);
        height = duration / 60 * timeSlotHeight;
        displayDuration = duration;
        if (target) {
            displayDate = target;
            top = getOffset(target);
        }
    }
    if (!allDay && dragging && getLocalDateKey(displayDate) !== getLocalDateKey(periodDate)) return null;
    if (!allDay && resizing && currentMouseY !== null && resizeStartY !== null && resizingTask) {
        const delta = Math.round((currentMouseY - resizeStartY) / (timeSlotHeight / 60) / 15) * 15;
        if (resizingTask.edge === 'top') {
            const actual = Math.min(delta, duration - 15);
            top += actual * timeSlotHeight / 60;
            height -= actual * timeSlotHeight / 60;
            displayDuration = duration - actual;
            displayDate = new Date(taskDate.getTime() + actual * 60_000);
        } else {
            displayDuration = Math.max(15, duration + delta);
            height = displayDuration / 60 * timeSlotHeight;
        }
    }
    const { start, end } = formatTaskTimeRange(displayDate, displayDuration);
    const startRatio = layout.column / layout.columnCount;
    const remainingRatio = 1 - (layout.column + 1) / layout.columnCount;
    const left = layout.columnCount === 1 ? '80px' : `calc(${startRatio * 100}% + ${80 * (1 - startRatio)}px)`;
    const right = layout.columnCount === 1 || remainingRatio === 0 ? '3px'
        : `calc(${remainingRatio * 100}% - ${80 * remainingRatio}px + 3px)`;
    const timeLabel = allDay ? t('dailyPlanner.allDay') : `${start} - ${end}`;
    const handleTaskPointerUp = () => {
        const shouldOpenActions = pointerStartY.current !== null && !pointerMoved.current;
        releaseTouchGesture();
        pointerStartY.current = null;
        if (shouldOpenActions) setShowActions(true);
    };
    return <>
        <div className={`timeline-task timeline-hour-task task-timed ${layout.columnCount > 1 ? 'overlapping' : ''} ${layout.columnCount > 2 ? 'crowded' : ''} ${task.completed ? 'completed' : ''} ${dragging ? 'dragging' : ''} ${resizing ? 'resizing' : ''}`}
            data-timeline-task-id={task.id} data-overlap-column={layout.column} data-overlap-count={layout.columnCount}
            onPointerDown={allDay ? (event) => event.stopPropagation() : (event) => {
                pointerStartY.current = event.clientY;
                pointerMoved.current = false;
                longPressActive.current = false;
                event.stopPropagation();
                const element = event.currentTarget;
                const { clientY, pointerId } = event;
                if (event.pointerType === 'touch') watchTouchMove(element);
                longPressTimer.current = window.setTimeout(() => {
                    longPressTimer.current = null;
                    longPressActive.current = true;
                    pointerMoved.current = true;
                    onTaskLongPress(task, collectionId, element, clientY, pointerId);
                }, TIMELINE_GESTURE_HOLD_DELAY);
            }}
            onPointerMove={(event) => {
                if (pointerStartY.current !== null && Math.abs(event.clientY - pointerStartY.current) > 8) {
                    pointerMoved.current = true;
                    clearLongPress();
                }
            }}
            onPointerUp={handleTaskPointerUp} onPointerCancel={() => {
                releaseTouchGesture();
                pointerStartY.current = null;
            }}
            style={{ top: `${top}px`, height: `${Math.max(24, height)}px`, left, right, zIndex: dragging || resizing ? 6 : 2 }}>
            {!allDay && <div className="resize-handle resize-handle-top" onPointerDown={(event) => startResizeHold('top', event)} onPointerMove={clearLongPress} />}
            <div className="timeline-task-info">
                <span className="task-title">{task.title}</span>
                <span className="task-interval">{timeLabel}</span>
            </div>
            {!allDay && <div className="resize-handle resize-handle-bottom" onPointerDown={(event) => startResizeHold('bottom', event)} onPointerMove={clearLongPress} />}
        </div>
        {showActions && <TimelineTaskModal task={task} collectionId={collectionId} timeLabel={timeLabel}
            onClose={() => setShowActions(false)} onDelete={onDeleteTask} onEdit={onEditTask} onToggleComplete={onToggleComplete} />}
    </>;
};
